import Loading from 'core/modules/atoms/Loading';
import SelectCustomizable from 'core/modules/atoms/SelectCustomizable';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import UnitMetricsCard from '../../components/compositions/UnitMetricsCard';
import UnitRecommendationsCard from '../../components/compositions/UnitRecommendationsCard';
import TeacherLayoutWrapper from '../../components/layouts/TeacherLayoutWrapper/TeacherLayoutWrapper';
import useTeacherAnalytics from '../../hooks/useTeacherAnalytics';
import AnalyticsErrorState from './AnalyticsErrorState';
import './teacher-analytics-view.scss';

/**
 * TeacherAnalyticsView - Main view component for teacher analytics
 */
const TeacherAnalyticsView = () => {
  const { t } = useTranslation(['chatbot']);
  const history = useHistory();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const { loading, eligibleCourses, analyticsData, recommendations, groups, currentGroup, selectedCourse, setSelectedCourse, classId } = useTeacherAnalytics();

  // Set default subject - reset to first course when class changes
  React.useEffect(() => {
    if (eligibleCourses.length > 0) {
      setSelectedCourse(eligibleCourses[0].guid);
    }
  }, [classId, eligibleCourses.length, setSelectedCourse]);

  // Memoize selected course option to avoid calling .find() twice on every render
  const selectedCourseOption = useMemo(() => {
    const course = eligibleCourses.find((s) => s.guid === selectedCourse);
    return course ? { label: course.disciplineName, value: course.guid } : null;
  }, [eligibleCourses, selectedCourse]);

  const onShowMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
    if (!showMobileMenu) {
      document.body.style.overflowY = 'hidden';
    } else document.body.style.overflowY = 'scroll';
  };

  // Render header with tabs and class selector
  const renderHeader = () => (
    <div className="teacher-analytics-view__header-container">
      {/* Subject Selector */}
      <div className="teacher-analytics-view__subject-selector">
        <SelectCustomizable
          options={eligibleCourses.map((subject) => ({
            label: subject.disciplineName,
            value: subject.guid,
          }))}
          value={selectedCourseOption}
          onChange={(option) => option && setSelectedCourse(option.value)}
          placeholder={t('chatbot:select_subject')}
          variant="default"
          size="default"
        />
      </div>

      {/* Class Selector */}
      <div className="teacher-analytics-view__class-selector">
        <SelectCustomizable
          options={groups.map((group) => ({
            label: group.name,
            value: group.guid,
          }))}
          value={{
            label: currentGroup?.name || '',
            value: currentGroup?.guid,
          }}
          onChange={(selectedOption) => {
            if (selectedOption) {
              history.push(`/analytics/${selectedOption.value}`);
            }
          }}
          placeholder={t('Select a class')}
          variant="default"
          size="default"
        />
      </div>
    </div>
  );

  // Render main content based on state
  const renderContent = () => {
    // No eligible courses or no analytics data available
    if (!loading && (!eligibleCourses || eligibleCourses.length === 0 || !analyticsData || analyticsData.length === 0)) {
      return <AnalyticsErrorState />;
    }

    // Analytics data available
    if (analyticsData && analyticsData.length > 0 && selectedCourse) {
      const subjectData = analyticsData.find((data) => data.courseId === selectedCourse);

      // If selected course has no data, show error state
      if (!subjectData || !subjectData.report || !subjectData.report.unitMetrics || subjectData.report.unitMetrics.length === 0) {
        return <AnalyticsErrorState />;
      }

      const unitMetrics = subjectData.report.unitMetrics;

      return (
        <div className="teacher-analytics-view__sections-container">
          {/* Diagnostics Section */}
          <div className="teacher-analytics-view__section">
            <h3 className="teacher-analytics-view__section-title">{t('analytics_section_diagnostics')}</h3>
            <div className="teacher-analytics-view__units-grid">
              {unitMetrics
                .sort((a, b) => a.order - b.order)
                .map((unit) => (
                  <UnitMetricsCard key={unit.unitId} unit={unit} totalStudents={unit.totalStudents || 0} />
                ))}
            </div>
            {/* Progress Bar Legend */}
            <div className="teacher-analytics-view__legend">
              <div className="teacher-analytics-view__legend-item">
                <span className="teacher-analytics-view__legend-color teacher-analytics-view__legend-color--mastered" />
                <span className="teacher-analytics-view__legend-text">{t('kc_mastered_full', { defaultValue: 'Contenidos dominados' })}</span>
              </div>
              <div className="teacher-analytics-view__legend-item">
                <span className="teacher-analytics-view__legend-color teacher-analytics-view__legend-color--in-progress" />
                <span className="teacher-analytics-view__legend-text">{t('kc_in_progress')}</span>
              </div>
              <div className="teacher-analytics-view__legend-item">
                <span className="teacher-analytics-view__legend-color teacher-analytics-view__legend-color--not-started" />
                <span className="teacher-analytics-view__legend-text">{t('kc_not_started_full', { defaultValue: 'Contenidos sin empezar' })}</span>
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="teacher-analytics-view__section-separator" />

          {/* Recommendations Section */}
          <div className="teacher-analytics-view__section">
            <h3 className="teacher-analytics-view__section-title">{t('analytics_section_recommendations')}</h3>
            <div className="teacher-analytics-view__recommendations">
              {recommendations
                .sort((a, b) => a.order - b.order)
                .map((unit) => (
                  <UnitRecommendationsCard key={unit.unitId} unit={unit} />
                ))}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Early return for loading state
  if (loading) {
    return <Loading />;
  }

  // Single TeacherLayoutWrapper with conditional content
  return (
    <TeacherLayoutWrapper activeNavigationId="analytics" showMobileMenu={showMobileMenu} onShowMobileMenu={onShowMobileMenu} className="teacher-analytics-view">
      {renderHeader()}
      {renderContent()}
    </TeacherLayoutWrapper>
  );
};

export default TeacherAnalyticsView;
